import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { createPortal } from 'react-dom';
import {
  UserTodoItem,
  GetUserTodosResponse,
  AgentEvents,
  UserTodoUpdatedEvent,
} from '@dreamai/types';
import { useAgent } from '../../contexts/AgentContext';
import { usePusher } from '../../contexts/PusherContext';
import { useOnboardingModals } from '../../providers';
import api from '../../api';
import TasksSkeleton from './sidebar/TasksSkeleton';
import { ReactComponent as PlusAddIcon } from '../../icons/plus-add.svg';
import { ReactComponent as CalendarOutlineIcon } from '../../icons/calendar-outline.svg';
import { ReactComponent as VerticalDotsIcon } from '../../icons/vertical-dots.svg';
import { ReactComponent as TodoistLogo } from '../../icons/todoist-logo.svg';
import './reminders-page.scss';

// ─── Helpers ──────────────────────────────────────────────────

const isOverdue = (dueDate: string): boolean => new Date(dueDate) < new Date();

const formatDueDate = (dueDate: string): string => {
  const d = new Date(dueDate);
  const datePart = d.toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  if (d.getHours() === 0 && d.getMinutes() === 0) return datePart;
  const timePart = d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .replace(':00', '');
  return `${datePart} at ${timePart}`;
};

const PRIORITY_COLORS: Record<string, string> = {
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#3B82F6',
};

// ─── Component ────────────────────────────────────────────────

const RemindersPage: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { activeAgent } = useAgent();
  const { hasSeenModal, markAsSeen } = useOnboardingModals();
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  const [userTodos, setUserTodos] = useState<UserTodoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [todoistConnected, setTodoistConnected] = useState(false);
  const [todoistLoading, setTodoistLoading] = useState(true);

  // Dropdown menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });
  const menuRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Animation state
  const [completingItems, setCompletingItems] = useState<Set<string>>(
    new Set(),
  );

  const todoSound = useMemo(() => new Audio('/todo-sound.mp3'), []);
  const playTodoSound = useCallback(() => {
    todoSound.currentTime = 0;
    todoSound.play().catch(() => {});
  }, [todoSound]);

  // ─── Data Fetching ────────────────────────────────────────────

  const fetchTodos = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const agentFilter = activeAgent?.id ? `&agentId=${activeAgent.id}` : '';
      const response = await api.get<GetUserTodosResponse>(
        `/user-todos?assigneeType=user${agentFilter}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUserTodos(response.data.todos || []);
    } catch (err) {
      console.error('Error fetching reminders:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, activeAgent?.id]);

  const fetchTodoistStatus = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/todoist-link', { headers: { Authorization: `Bearer ${token}` } });
      setTodoistConnected(res.data.linkedAccounts.length > 0);
    } catch (err) {
      console.error('Error fetching Todoist status:', err);
    } finally {
      setTodoistLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchTodos();
    fetchTodoistStatus();
  }, [fetchTodos, fetchTodoistStatus]);

  // ─── Real-time Pusher updates ─────────────────────────────────

  useEffect(() => {
    if (!userChannel) return;

    const handleUserTodoUpdated = (data: UserTodoUpdatedEvent) => {
      if (!data.todoId) return;
      setUserTodos((prev) => {
        const exists = prev.some((t) => t.id === data.todoId);
        if (!exists) {
          fetchTodos();
          return prev;
        }
        return prev.map((t) => {
          if (t.id !== data.todoId) return t;
          const updates: Partial<UserTodoItem> = {};
          if ((data as any).priority !== undefined)
            updates.priority = (data as any).priority;
          if ((data as any).dueDate !== undefined)
            updates.dueDate = (data as any).dueDate;
          if ((data as any).description !== undefined)
            updates.description = (data as any).description;
          if (data.status !== undefined)
            updates.status = data.status as UserTodoItem['status'];
          return { ...t, ...updates };
        });
      });
    };

    userChannel.bind(AgentEvents.USER_TODO_UPDATED, handleUserTodoUpdated);
    return () => {
      userChannel.unbind(AgentEvents.USER_TODO_UPDATED, handleUserTodoUpdated);
    };
  }, [userChannel, fetchTodos]);

  // ─── Dropdown Menu ────────────────────────────────────────────

  const toggleMenu = useCallback(
    (id: string, btnEl: HTMLElement) => {
      if (openMenuId === id) {
        setOpenMenuId(null);
        return;
      }
      const rect = btnEl.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
      setOpenMenuId(id);
    },
    [openMenuId],
  );

  useEffect(() => {
    if (!openMenuId) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setOpenMenuId(null);
    };
    const handleScroll = () => setOpenMenuId(null);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [openMenuId]);

  // ─── Actions ──────────────────────────────────────────────────

  const handleToggleTodo = useCallback(
    async (todo: UserTodoItem) => {
      const newStatus = todo.status === 'pending' ? 'complete' : 'pending';

      if (newStatus === 'complete') {
        playTodoSound();
        setCompletingItems((prev) => new Set(prev).add(todo.id));

        const todoEl = document.querySelector(
          `[data-reminder-id="${todo.id}"]`,
        ) as HTMLElement | null;
        if (todoEl) {
          setTimeout(() => todoEl.classList.add('collapsed'), 700);
        }

        setTimeout(() => {
          setCompletingItems((prev) => {
            const next = new Set(prev);
            next.delete(todo.id);
            return next;
          });
          setUserTodos((prev) =>
            prev.map((t) =>
              t.id === todo.id ? { ...t, status: newStatus } : t,
            ),
          );
        }, 1100);
      } else {
        setUserTodos((prev) =>
          prev.map((t) => (t.id === todo.id ? { ...t, status: newStatus } : t)),
        );
      }

      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/user-todos/${todo.id}`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Error updating reminder:', err);
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(todo.id);
          return next;
        });
        setUserTodos((prev) =>
          prev.map((t) =>
            t.id === todo.id ? { ...t, status: todo.status } : t,
          ),
        );
      }
    },
    [getAccessTokenSilently, playTodoSound],
  );

  const handleDismissTodo = useCallback(
    async (todo: UserTodoItem) => {
      setOpenMenuId(null);
      setCompletingItems((prev) => new Set(prev).add(todo.id));

      const todoEl = document.querySelector(
        `[data-reminder-id="${todo.id}"]`,
      ) as HTMLElement | null;
      if (todoEl) {
        setTimeout(() => todoEl.classList.add('collapsed'), 700);
      }

      setTimeout(() => {
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(todo.id);
          return next;
        });
        setUserTodos((prev) => prev.filter((t) => t.id !== todo.id));
      }, 1100);

      try {
        const token = await getAccessTokenSilently();
        await api.delete(`/user-todos/${todo.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        console.error('Error dismissing reminder:', err);
      }
    },
    [getAccessTokenSilently],
  );

  const handleSetPriority = useCallback(
    async (todo: UserTodoItem, priority: string | null) => {
      setOpenMenuId(null);
      const typedPriority = priority
        ? (priority as 'high' | 'medium' | 'low')
        : undefined;

      setUserTodos((prev) =>
        prev.map((t) =>
          t.id === todo.id ? { ...t, priority: typedPriority } : t,
        ),
      );

      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/user-todos/${todo.id}`,
          { priority },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Error setting priority:', err);
      }
    },
    [getAccessTokenSilently],
  );

  const handleDismissTodoistBanner = useCallback(() => {
    markAsSeen('reminders_todoist_cta');
  }, [markAsSeen]);

  const handleConnectTodoist = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'reminders',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/todoist-link/initiate-todoist-oauth?${params}`;
  }, [user]);

  // ─── Sorted items ─────────────────────────────────────────────

  const activeReminders = useMemo(() => {
    const items = userTodos.filter((t) => t.status === 'pending');

    const PRIORITY_TIER: Record<string, number> = {
      high: 2,
      medium: 3,
      low: 4,
    };

    items.sort((a, b) => {
      const aTier = a.priority
        ? (PRIORITY_TIER[a.priority] ?? 5)
        : a.dueDate
          ? 5
          : 6;
      const bTier = b.priority
        ? (PRIORITY_TIER[b.priority] ?? 5)
        : b.dueDate
          ? 5
          : 6;
      if (aTier !== bTier) return aTier - bTier;
      if (a.dueDate && b.dueDate) {
        const diff =
          new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        if (diff !== 0) return diff;
      } else if (a.dueDate) return -1;
      else if (b.dueDate) return 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return items;
  }, [userTodos]);

  // ─── Todoist banner dismissed check ───────────────────────────

  const todoistBannerDismissed = hasSeenModal('reminders_todoist_cta');

  // ─── Render ───────────────────────────────────────────────────

  if (loading) {
    return (
      <div className='reminders-page'>
        <TasksSkeleton />
      </div>
    );
  }

  return (
    <div className='reminders-page'>
      <div className='reminders-section'>
        <div className='reminders-header'>
          <h1 className='reminders-heading'>Reminders</h1>
        </div>

        {/* Todoist banner — shown at top */}
        {!todoistConnected && !todoistBannerDismissed && !todoistLoading && (
          <div className='todoist-banner'>
            <TodoistLogo className='todoist-icon' />
            <div className='todoist-banner-mid'>
              <h3 className='todoist-banner-title'>Connect Todoist</h3>
              <p className='todoist-banner-desc'>
                <button
                  className='todoist-connect-link'
                  onClick={handleConnectTodoist}
                >
                  Connect your Todoist account
                </button>{' '}
                and your assistant will use that to store reminders and will be
                able to manage your todo list according to your requirements.
              </p>
            </div>
            <button
              className='todoist-banner-close'
              onClick={handleDismissTodoistBanner}
              aria-label='Dismiss'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <path
                  d='M12 4L4 12M4 4L12 12'
                  stroke='black'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
          </div>
        )}
        {todoistConnected && (
          <div className='todoist-banner'>
            <TodoistLogo className='todoist-icon' />
            <div className='todoist-banner-mid'>
              <h3 className='todoist-banner-title'>
                Your reminders are managed on Todoist
              </h3>
              <p className='todoist-banner-desc'>
                You can ask your assistant to add, read or mark as done your
                todos on Todoist. If you ask your assistant to remind you about
                something they'll add it to Todoist and proactively remind you
                themselves.
              </p>
            </div>
          </div>
        )}

        {/* Incomplete section header */}
        <div className='reminders-incomplete-header'>
          <h2 className='reminders-incomplete-title'>Incomplete</h2>
          <span className='reminders-incomplete-count'>
            {activeReminders.length}
          </span>
          <button className='reminders-new-btn' onClick={() => {}}>
            <PlusAddIcon />
            <span>New</span>
          </button>
        </div>

        <div className='reminders-list'>
          {activeReminders.length === 0 && (
            <div className='reminders-empty'>
              <div className='reminders-empty-icon'>
                <img
                  src='/images/empty-reminders.png'
                  alt=''
                  width={72}
                  height={72}
                />
              </div>
              <h3 className='reminders-empty-title'>
                You don't have any Reminders
              </h3>
              <div className='reminders-empty-desc'>
                <p>Ask your Assistant, or choose 'New'.</p>
                <p>Open Settings to import your reminders from another app.</p>
              </div>
            </div>
          )}
          {activeReminders.map((todo) => {
            const isCompleting = completingItems.has(todo.id);
            const projectName = todo.threadDescription || null;

            return (
              <div
                key={todo.id}
                className={`reminder-item${isCompleting ? ' completing' : ''}`}
                data-reminder-id={todo.id}
              >
                <button
                  className='reminder-circle'
                  onClick={() => handleToggleTodo(todo)}
                  aria-label='Mark as done'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <circle
                      cx='12'
                      cy='12'
                      r='11.5'
                      fill={isCompleting ? '#489FD9' : '#FFFEFC'}
                      stroke={isCompleting ? '#489FD9' : '#C1C5CD'}
                    />
                    {isCompleting && (
                      <path
                        className='tick'
                        d='M7 12L10.5 15.5L17 9'
                        stroke='white'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        fill='none'
                      />
                    )}
                  </svg>
                </button>

                <div className='reminder-mid'>
                  <p className='reminder-description'>
                    {todo.priority && PRIORITY_COLORS[todo.priority] && (
                      <span
                        className='priority-dot'
                        style={{
                          background: PRIORITY_COLORS[todo.priority],
                        }}
                      />
                    )}
                    {todo.description}
                  </p>
                  <div className='reminder-meta'>
                    {todo.dueDate && (
                      <span
                        className={`reminder-due${isOverdue(todo.dueDate) ? ' overdue' : ''}`}
                      >
                        <CalendarOutlineIcon className='calendar-icon' />
                        {formatDueDate(todo.dueDate)}
                      </span>
                    )}
                    {projectName && (
                      <span className='reminder-project'>{projectName}</span>
                    )}
                  </div>
                </div>

                <div
                  className='reminder-right'
                  ref={openMenuId === todo.id ? menuRef : undefined}
                >
                  <button
                    className='dots-btn'
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMenu(todo.id, e.currentTarget);
                    }}
                  >
                    <VerticalDotsIcon />
                  </button>
                  {openMenuId === todo.id &&
                    createPortal(
                      <div
                        className='reminders-dropdown-menu'
                        ref={dropdownRef}
                        style={{ top: menuPos.top, right: menuPos.right }}
                      >
                        <div className='dropdown-item has-submenu'>
                          <span>Set priority</span>
                          <span className='submenu-arrow'>›</span>
                          <div className='priority-submenu'>
                            {[
                              {
                                value: 'high',
                                label: 'High',
                                color: '#EF4444',
                              },
                              {
                                value: 'medium',
                                label: 'Medium',
                                color: '#F59E0B',
                              },
                              {
                                value: 'low',
                                label: 'Low',
                                color: '#3B82F6',
                              },
                              {
                                value: null,
                                label: 'No priority',
                                color: '#838b9b',
                              },
                            ].map((opt) => (
                              <div
                                key={opt.value ?? 'none'}
                                className={`priority-option${(todo.priority || null) === opt.value ? ' active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSetPriority(todo, opt.value);
                                }}
                              >
                                <span
                                  className='priority-dot'
                                  style={{ background: opt.color }}
                                />
                                {opt.label}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div
                          className='dropdown-item dismiss'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDismissTodo(todo);
                          }}
                        >
                          Dismiss
                        </div>
                      </div>,
                      document.body,
                    )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RemindersPage;
